import { defineStore, Store } from 'pinia'

import { ExportConfig } from '@/api/fields'
import { IColumnVisibility, ISchemaField } from '@/types/entities.type'
import { IViewConfig } from '@/types/serverField.type'

type ConfigStore = Store<
  string,
  { id: string, options: any, config: IViewConfig, visibleColumns: string[] | null, columnsCustomized: boolean },
  { export(): ExportConfig | null, schema(): ISchemaField[], columns(): IColumnVisibility },
  { initColumns(): void }
>

const stores: Record<string, ConfigStore> = {}

export const useConfigStore = (id: string) => {
  if (stores[id]) {
    return stores[id]
  }

  const STORAGE_KEY = `columns:${id}`

  stores[id] = defineStore(`config:${id}`, {
    state (): { id: string, options: any, config: IViewConfig, visibleColumns: string[] | null, columnsCustomized: boolean } {
      return {
        id,
        options: {},
        config: {} as IViewConfig,
        visibleColumns: null,
        columnsCustomized: false,
      }
    },

    getters: {
      export (): ExportConfig | null {
        if (!this.config) return null
        return new ExportConfig({
          exportUrl: this.config.Export.ExportUrl,
          options: this.config.Export.Options,
        })
      },

      schema (): ISchemaField[] {
        return this.config?.Schema?.Fields ?? []
      },

      // Итоговый Set видимых колонок - с учётом localStorage и дефолтов
      columns (): IColumnVisibility {
        const visible = new Set(this.visibleColumns ?? [])
        const schema = this.schema
        const id = this.id
        return {
          schema,
          isVisible: (machineName: string) => {
            const field = schema.find(f => f.MachineName === machineName)
            if (!field) return true // служебные колонки всегда видны
            return visible.has(machineName)
          },
          toggle: (machineName: string) => {
            const field = schema.find(f => f.MachineName === machineName)
            if (!field || field.Required) return

            const current = new Set(visible)
            if (current.has(machineName)) {
              current.delete(machineName)
            } else {
              current.add(machineName)
            }

            const next = [...current]

            // Обновляем стор напрямую
            stores[id]?.$patch({ visibleColumns: next, columnsCustomized: true })
            localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
          },
        }
      },
    },

    actions: {
      // Вызывается после того как config загружен с бэка
      initColumns () {
        const schema: ISchemaField[] = this.config?.Schema?.Fields ?? []
        if (!schema.length) return

        try {
          const saved = localStorage.getItem(STORAGE_KEY)
          if (saved) {
            const parsed: string[] = JSON.parse(saved)
            // Required всегда добавляем поверх сохранённых
            const required = schema.filter(f => f.Required).map(f => f.MachineName)
            this.visibleColumns = [...new Set([...required, ...parsed])]
            this.columnsCustomized = true
            return
          }
        } catch {}

        // Дефолт из схемы
        this.visibleColumns = schema
          .filter(f => f.Default || f.Required)
          .map(f => f.MachineName)
      }
    },
  })()

  return stores[id]
}
